import React, { useState, useEffect, useContext, useCallback } from 'react';
import AuthContext from '../context/AuthContext';
import {
  Box, Container, Typography, Button, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Tooltip, Paper, Autocomplete, TextField,
  Snackbar, Alert, Switch, FormControlLabel, Checkbox, ListItemText, Chip, OutlinedInput,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import CakeIcon from '@mui/icons-material/Cake';
import PrintIcon from '@mui/icons-material/Print';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { elUpperCase } from '../utils/text';

import { NAV_BG, AMBER, AMBER_DARK, AMBER_GLOW } from '../theme';

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getWeekRows(year, month) {
  const rows = [];
  const d = new Date(year, month, 1);
  let week = new Array(7).fill(null);
  while (d.getMonth() === month) {
    const dow = (d.getDay() + 6) % 7;
    week[dow] = new Date(d);
    if (dow === 6) { rows.push(week); week = new Array(7).fill(null); }
    d.setDate(d.getDate() + 1);
  }
  if (week.some(Boolean)) rows.push(week);
  return rows;
}

// ── Day cell ──────────────────────────────────────────────────────────────────
const DayCell = ({ date, items, onEdit, isToday, mealTypes, editMealsLabel }) => {
  const byType = {};
  items.forEach((it) => { byType[it.menuitem_type] = it; });
  const hasAny = Object.keys(byType).length > 0;

  return (
    <Box sx={{
      minHeight: 90,
      borderRadius: '10px',
      border: `1px solid ${isToday ? AMBER : '#e2e8f0'}`,
      background: isToday ? 'rgba(245,158,11,0.04)' : '#fff',
      p: '6px 8px',
      position: 'relative',
      transition: 'box-shadow 0.15s',
      '&:hover': { boxShadow: '0 2px 12px rgba(15,45,94,0.1)' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: isToday ? 700 : 500, fontSize: '0.78rem', color: isToday ? AMBER_DARK : NAV_BG, lineHeight: 1 }}>
          {date.getDate()}
        </Typography>
        <Tooltip title={editMealsLabel} arrow>
          <IconButton size="small" onClick={() => onEdit(date)} sx={{ p: 0.25, color: hasAny ? AMBER_DARK : '#cbd5e1', '&:hover': { color: AMBER, background: 'rgba(245,158,11,0.1)' } }}>
            <EditIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Tooltip>
      </Box>
      {mealTypes.map(({ value, icon: Icon, color }) => {
        const item = byType[value];
        const text = item?.choice1;
        if (!text) return null;
        return (
          <Box key={value} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.15 }}>
            <Icon sx={{ fontSize: 10, color, flexShrink: 0 }} />
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.68rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {text}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const FoodPage = () => {
  const { authTokens, logoutUser, backendUrl, pageTitle } = useContext(AuthContext);
  const { t } = useTranslation();
  document.title = (pageTitle || 'App') + ' - ' + t('food.title');

  const MEAL_TYPES = [
    { value: 0, label: t('food.breakfast'), icon: FreeBreakfastIcon, color: '#f59e0b' },
    { value: 1, label: t('food.lunch'), icon: LunchDiningIcon, color: '#10b981' },
    { value: 2, label: t('food.afternoon'), icon: CakeIcon, color: '#8b5cf6' },
  ];

  const MONTH_NAMES = Array.from({ length: 12 }, (_, i) => t(`food.months.${i}`));
  const DAY_NAMES = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map(d => t(`food.days.${d}`));
  const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [menuItems, setMenuItems] = useState([]);
  const [mealOptions, setMealOptions] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [toast, setToast] = useState({ msg: '', severity: 'success' });
  const showToast = (msg, severity = 'success') => setToast({ msg, severity });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDate, setDialogDate] = useState(null);
  const [dialogForm, setDialogForm] = useState({ 0: '', 1: '', 2: '' });
  const [extraClassIds, setExtraClassIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const [showWeekends, setShowWeekends] = useState(false);

  const [copyOpen, setCopyOpen] = useState(false);
  const [srcYear, setSrcYear] = useState(today.getFullYear());
  const [srcMonth, setSrcMonth] = useState(today.getMonth());
  const [copyClassId, setCopyClassId] = useState('');
  const [copying, setCopying] = useState(false);

  const [clearOpen, setClearOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + String(authTokens.access) });
  const handleUnauth = (r) => { if (r.status === 401) { logoutUser(); return true; } return false; };

  useEffect(() => {
    fetch(backendUrl + 'api/get-classes/', { headers: authHeader() })
      .then((r) => { if (handleUnauth(r)) return null; return r.json(); })
      .then((data) => {
        if (!data) return;
        setClasses(data);
        const first = data[0];
        if (first) setSelectedClassId(String(first.id));
        setLoadingClasses(false);
      })
      .catch(() => showToast(t('common.network_error'), 'error'));
  }, []);

  useEffect(() => {
    fetch(backendUrl + 'api/meal-options/', { headers: authHeader() })
      .then((r) => { if (handleUnauth(r)) return null; return r.json(); })
      .then((data) => { if (data) setMealOptions(data); })
      .catch(() => showToast(t('common.network_error'), 'error'));
  }, []);

  const loadMenu = useCallback(async () => {
    if (!selectedClassId) return;
    setLoadingMenu(true);
    try {
      const r = await fetch(backendUrl + `api/menu/?year=${year}&month=${month + 1}&schoolclass_id=${selectedClassId}`, { headers: authHeader() });
      if (handleUnauth(r)) return;
      if (r.ok) setMenuItems(await r.json());
      else showToast(t('common.network_error'), 'error');
    } catch {
      showToast(t('common.network_error'), 'error');
    } finally {
      setLoadingMenu(false);
    }
  }, [selectedClassId, year, month]);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  const prevMonth = () => { if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1); };

  const openEdit = (date) => {
    const dateStr = toISODate(date);
    const form = { 0: '', 1: '', 2: '' };
    menuItems.forEach((it) => { if (it.date === dateStr) form[it.menuitem_type] = it.choice1 || ''; });
    setDialogForm(form);
    setDialogDate(date);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const dateStr = toISODate(dialogDate);
    const allClassIds = [selectedClassId, ...extraClassIds];
    try {
      await Promise.all(
        allClassIds.flatMap(classId =>
          MEAL_TYPES.map(({ value }) =>
            fetch(backendUrl + 'api/save-menu-item/', {
              method: 'POST',
              headers: authHeader(),
              body: JSON.stringify({ schoolclass_id: classId, date: dateStr, menuitem_type: value, meal: dialogForm[value] }),
            })
          )
        )
      );
      fetch(backendUrl + 'api/meal-options/', { headers: authHeader() })
        .then(r => r.json()).then(data => { if (data) setMealOptions(data); }).catch(() => showToast(t('common.network_error'), 'error'));
      setDialogOpen(false);
      setExtraClassIds([]);
      loadMenu();
    } catch {
      showToast(t('common.network_error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const openCopyDialog = () => {
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    setSrcMonth(pm);
    setSrcYear(py);
    setCopyClassId(selectedClassId);
    setCopyOpen(true);
  };

  const handleCopy = async () => {
    setCopying(true);
    try {
      const r = await fetch(backendUrl + 'api/copy-month-menu/', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ schoolclass_id: copyClassId, src_year: srcYear, src_month: srcMonth + 1, dst_year: year, dst_month: month + 1 }),
      });
      if (handleUnauth(r)) { setCopying(false); setCopyOpen(false); return; }
      const data = await r.json();
      setCopying(false);
      setCopyOpen(false);
      if (r.ok) {
        if ((data.copied ?? 0) === 0) {
          showToast(t('food.copy_empty_source'), 'warning');
        } else {
          showToast(t('food.copied_message', { count: data.copied, month: MONTH_NAMES[month], year }));
          loadMenu();
        }
      } else {
        showToast(data.error || t('food.copy_failed'), 'error');
      }
    } catch (e) {
      setCopying(false);
      setCopyOpen(false);
      showToast(t('food.copy_failed'), 'error');
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      const r = await fetch(backendUrl + 'api/clear-month-menu/', {
        method: 'POST',
        headers: authHeader(),
        body: JSON.stringify({ schoolclass_id: selectedClassId, year, month: month + 1 }),
      });
      if (handleUnauth(r)) { setClearing(false); setClearOpen(false); return; }
      const data = await r.json();
      setClearing(false);
      setClearOpen(false);
      if (r.ok) {
        showToast(t('food.cleared_message', { month: MONTH_NAMES[month], year }));
        loadMenu();
      } else {
        showToast(data.error || t('food.clear_failed'), 'error');
      }
    } catch {
      setClearing(false);
      setClearOpen(false);
      showToast(t('food.clear_failed'), 'error');
    }
  };

  // ── Print helpers (browser-based, supports Greek) ─────────────────────────
  const printStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Outfit', Arial, sans-serif; padding: 24px; color: #1e293b; }
    h1 { color: #0f2d5e; font-size: 18px; margin-bottom: 4px; }
    p.subtitle { color: #64748b; font-size: 12px; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #0f2d5e; color: #f59e0b; font-weight: 700; padding: 8px 10px; text-align: left; font-size: 11px; }
    td { border: 1px solid #e2e8f0; padding: 7px 10px; font-size: 11px; vertical-align: top; }
    td.label-col { background: #f8fafc !important; color: #0f2d5e; font-weight: 600; white-space: nowrap; }
    tr.today td { background: #fffbeb; }
    tr.today td.label-col { color: #b45309; }
    @media print { body { padding: 12px; } @page { margin: 12mm; } }
  `;

  const openPrintWindow = (title, bodyHtml) => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${title}</title><style>${printStyles}</style></head><body>${bodyHtml}</body></html>`);
    win.document.close();
    setTimeout(() => { win.focus(); win.print(); }, 400);
  };

  const printWeek = () => {
    const selectedClass = classes.find(c => String(c.id) === selectedClassId);
    if (!selectedClass) return;
    const locale = i18n.language === 'el' ? 'el-GR' : 'en-GB';
    const dow = (today.getDay() + 6) % 7;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - dow);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 4);
    const dayNames = ['mon', 'tue', 'wed', 'thu', 'fri'].map(k => t(`food.days.${k}`));
    const mealLabels = [t('food.breakfast'), t('food.lunch'), t('food.afternoon')];
    const subtitle = `${selectedClass.name} · ${weekStart.toLocaleDateString(locale, { day: 'numeric', month: 'long' })} – ${weekEnd.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}`;
    const headCols = dayNames.map((name, di) => {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + di);
      return `<th>${name}<br><span style="font-weight:400">${date.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}</span></th>`;
    }).join('');
    const bodyRows = [0, 1, 2].map(typeIdx => {
      const cols = Array.from({ length: 5 }, (_, di) => {
        const date = new Date(weekStart);
        date.setDate(weekStart.getDate() + di);
        const item = menuItems.find(it => it.date === toISODate(date) && it.menuitem_type === typeIdx);
        return `<td>${item?.choice1 || ''}</td>`;
      }).join('');
      return `<tr><td class="label-col">${mealLabels[typeIdx]}</td>${cols}</tr>`;
    }).join('');
    openPrintWindow(t('food.pdf_week_title'), `<h1>${t('food.pdf_week_title')}</h1><p class="subtitle">${subtitle}</p><table><thead><tr><th></th>${headCols}</tr></thead><tbody>${bodyRows}</tbody></table>`);
  };

  const printMonth = () => {
    const selectedClass = classes.find(c => String(c.id) === selectedClassId);
    if (!selectedClass) return;
    const locale = i18n.language === 'el' ? 'el-GR' : 'en-GB';
    const monthName = t(`food.months.${month}`);
    const mealLabels = [t('food.breakfast'), t('food.lunch'), t('food.afternoon')];
    const dayLabel = t('food.pdf_day');
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const rows = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      if ((date.getDay() + 6) % 7 >= 5) continue;
      const dateStr = toISODate(date);
      const dayName = date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric' });
      const meals = [0, 1, 2].map(t => menuItems.find(it => it.date === dateStr && it.menuitem_type === t)?.choice1 || '');
      rows.push({ dayName, meals, isToday: dateStr === toISODate(today) });
    }
    const headRow = `<tr><th>${dayLabel}</th>${mealLabels.map(l => `<th>${l}</th>`).join('')}</tr>`;
    const bodyRows = rows.map(r => `<tr class="${r.isToday ? 'today' : ''}"><td class="label-col">${r.dayName}</td>${r.meals.map(m => `<td>${m}</td>`).join('')}</tr>`).join('');
    openPrintWindow(t('food.pdf_month_title'), `<h1>${t('food.pdf_month_title')}</h1><p class="subtitle">${selectedClass.name} · ${monthName} ${year}</p><table><thead>${headRow}</thead><tbody>${bodyRows}</tbody></table>`);
  };

  const weekRows = getWeekRows(year, month);
  const todayStr = toISODate(today);
  const menuByDate = {};
  menuItems.forEach((it) => { (menuByDate[it.date] ||= []).push(it); });
  const visibleDayIndices = showWeekends ? [0, 1, 2, 3, 4, 5, 6] : [0, 1, 2, 3, 4];
  const gridCols = showWeekends ? 7 : 5;

  const acSx = (color) => ({
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: color },
    '& .MuiInputLabel-root.Mui-focused': { color },
  });

  const locale = i18n.language === 'el' ? 'el-GR' : 'en-GB';

  if (loadingClasses) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
      <CircularProgress size={44} thickness={3} sx={{ color: AMBER }} />
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: '#f1f5f9', pt: 4, pb: 6 }}>
      <Container maxWidth="xl">

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, borderRadius: '12px', background: `linear-gradient(135deg, ${NAV_BG} 0%, #163571 100%)`, boxShadow: '0 4px 14px rgba(15,45,94,0.25)', flexShrink: 0 }}>
              <RestaurantIcon sx={{ color: AMBER, fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.5rem', color: NAV_BG, lineHeight: 1.2 }}>{t('food.title')}</Typography>
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#94a3b8', mt: 0.1 }}>{t('food.subtitle')}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>{t('food.class')}</InputLabel>
              <Select value={selectedClassId} label={t('food.class')} onChange={(e) => setSelectedClassId(e.target.value)}
                sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', borderRadius: '10px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: AMBER }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AMBER } }}>
                {classes.map((sc) => (
                  <MenuItem key={sc.id} value={String(sc.id)} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>{sc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={t('food.copy_tooltip')} arrow>
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={openCopyDialog}
                sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', borderRadius: '10px', borderColor: '#e2e8f0', color: NAV_BG, '&:hover': { borderColor: AMBER, background: 'rgba(245,158,11,0.04)' } }}>
                {t('food.copy_month')}
              </Button>
            </Tooltip>
            <Tooltip title={t('food.clear_tooltip')} arrow>
              <Button variant="outlined" startIcon={<DeleteSweepIcon />} onClick={() => setClearOpen(true)}
                sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', borderRadius: '10px', borderColor: '#e2e8f0', color: '#dc2626', '&:hover': { borderColor: '#dc2626', background: 'rgba(220,38,38,0.04)' } }}>
                {t('food.clear_month')}
              </Button>
            </Tooltip>
            <Tooltip title={t('food.export_week_tooltip')} arrow>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={printWeek}
                sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', borderRadius: '10px', borderColor: '#e2e8f0', color: '#dc2626', '&:hover': { borderColor: '#dc2626', background: 'rgba(220,38,38,0.04)' } }}>
                {t('food.export_week')}
              </Button>
            </Tooltip>
            <Tooltip title={t('food.export_month_tooltip')} arrow>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={printMonth}
                sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.875rem', textTransform: 'none', borderRadius: '10px', borderColor: '#e2e8f0', color: '#dc2626', '&:hover': { borderColor: '#dc2626', background: 'rgba(220,38,38,0.04)' } }}>
                {t('food.export_month')}
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Month navigator ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={prevMonth} size="small" sx={{ color: NAV_BG, '&:hover': { background: 'rgba(15,45,94,0.06)' } }}><NavigateBeforeIcon /></IconButton>
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: NAV_BG, minWidth: 180, textAlign: 'center' }}>
            {MONTH_NAMES[month]} {year}
          </Typography>
          <IconButton onClick={nextMonth} size="small" sx={{ color: NAV_BG, '&:hover': { background: 'rgba(15,45,94,0.06)' } }}><NavigateNextIcon /></IconButton>
          {loadingMenu && <CircularProgress size={18} thickness={3} sx={{ color: AMBER, ml: 1 }} />}
          <Box sx={{ ml: 'auto' }}>
            <FormControlLabel
              control={
                <Switch checked={showWeekends} onChange={(e) => setShowWeekends(e.target.checked)} size="small"
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AMBER }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: AMBER } }} />
              }
              label={<Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.8rem', color: '#64748b' }}>{t('food.show_weekends')}</Typography>}
            />
          </Box>
        </Box>

        {/* ── Legend ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          {MEAL_TYPES.map(({ label, icon: Icon, color }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icon sx={{ fontSize: 14, color }} />
              <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', color: '#64748b' }}>{label}</Typography>
            </Box>
          ))}
        </Box>

        {/* ── Calendar ── */}
        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(15,45,94,0.07)' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${gridCols}, 1fr)`, background: `linear-gradient(180deg, ${NAV_BG} 0%, #163571 100%)` }}>
            {DAY_NAMES.filter((_, i) => visibleDayIndices.includes(i)).map((d, i) => (
              <Box key={i} sx={{ py: 1, textAlign: 'center' }}>
                <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.72rem', letterSpacing: '0.05em', color: (visibleDayIndices[i] === 5 || visibleDayIndices[i] === 6) ? 'rgba(245,158,11,0.45)' : 'rgba(241,245,249,0.85)' }}>
                  {elUpperCase(d)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ p: 1.5, background: '#f8fafc' }}>
            {weekRows.map((week, wi) => (
              <Box key={wi} sx={{ display: 'grid', gridTemplateColumns: `repeat(${gridCols}, 1fr)`, gap: 1, mb: wi < weekRows.length - 1 ? 1 : 0 }}>
                {week.filter((_, di) => visibleDayIndices.includes(di)).map((date, i) => {
                  const di = visibleDayIndices[i];
                  if (!date) return <Box key={di} />;
                  const dateStr = toISODate(date);
                  return (
                    <Box key={di}>
                      <DayCell date={date} items={menuByDate[dateStr] || []} onEdit={openEdit} isToday={dateStr === todayStr} mealTypes={MEAL_TYPES} editMealsLabel={t('food.edit_meals')} />
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Paper>

        {/* ── Edit dialog ── */}
        <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setExtraClassIds([]); }} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 }, onKeyDown: (e) => { if (e.key === 'Enter' && !e.defaultPrevented && !saving) handleSave(); } }}>
          <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, pb: 1 }}>
            {dialogDate && new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long' }).format(dialogDate)}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              {MEAL_TYPES.map(({ value, label, icon: Icon, color }) => (
                <Box key={value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                    <Icon sx={{ fontSize: 17, color }} />
                    <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.875rem', color: NAV_BG }}>{label}</Typography>
                  </Box>
                  <Autocomplete
                    freeSolo
                    options={Array.isArray(mealOptions) ? mealOptions : (mealOptions[String(value)] || [])}
                    inputValue={dialogForm[value]}
                    onChange={(_, newValue) => {
                      if (newValue !== null) {
                        setDialogForm(prev => ({ ...prev, [value]: typeof newValue === 'string' ? newValue : '' }));
                      }
                    }}
                    onInputChange={(_, newValue, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        setDialogForm(prev => ({ ...prev, [value]: newValue }));
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} size="small" placeholder={t(`food.${value === 0 ? 'breakfast' : value === 1 ? 'lunch' : 'afternoon'}`)} sx={acSx(color)} />
                    )}
                    ListboxProps={{ sx: { fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' } }}
                  />
                </Box>
              ))}

              {classes.filter(c => String(c.id) !== selectedClassId).length > 0 && (
                <Box>
                  <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: '0.8rem', color: '#64748b', mb: 0.75 }}>
                    {t('food.also_apply_to')}
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select
                      multiple
                      displayEmpty
                      value={extraClassIds}
                      onChange={(e) => setExtraClassIds(e.target.value)}
                      input={<OutlinedInput />}
                      renderValue={(selected) =>
                        selected.length === 0
                          ? <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.85rem', color: '#94a3b8' }}>{t('food.no_extra_classes')}</Typography>
                          : <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map(id => {
                                const cls = classes.find(c => String(c.id) === id);
                                return <Chip key={id} label={cls?.name || id} size="small" sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.75rem', height: 22 }} />;
                              })}
                            </Box>
                      }
                      sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', borderRadius: '8px', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AMBER } }}
                    >
                      {classes.filter(c => String(c.id) !== selectedClassId).map(c => (
                        <MenuItem key={c.id} value={String(c.id)} sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' }}>
                          <Checkbox checked={extraClassIds.includes(String(c.id))} size="small" sx={{ color: AMBER, '&.Mui-checked': { color: AMBER } }} />
                          <ListItemText primary={c.name} primaryTypographyProps={{ sx: { fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem' } }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setDialogOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b', borderRadius: '8px' }}>{t('food.cancel')}</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: `linear-gradient(135deg, ${AMBER} 0%, ${AMBER_DARK} 100%)`, color: NAV_BG, boxShadow: `0 4px 14px ${AMBER_GLOW}`, '&:hover': { background: `linear-gradient(135deg, #fbbf24 0%, ${AMBER} 100%)` } }}>
              {saving ? t('food.saving') : t('food.save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Copy month dialog ── */}
        <Dialog open={copyOpen} onClose={() => setCopyOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
          <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG, pb: 1 }}>
            {t('food.copy_dialog_title', { month: MONTH_NAMES[month], year })}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: '#475569', mb: 2 }}>
              {t('food.copy_dialog_body', { month: MONTH_NAMES[month], year })}
            </Typography>
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ fontFamily: '"Outfit", sans-serif' }}>{t('food.copy_class_label')}</InputLabel>
              <Select value={copyClassId} label={t('food.copy_class_label')} onChange={(e) => setCopyClassId(e.target.value)}
                sx={{ fontFamily: '"Outfit", sans-serif', borderRadius: '8px', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AMBER } }}>
                <MenuItem value="all" sx={{ fontFamily: '"Outfit", sans-serif' }}>{t('food.copy_all_classes')}</MenuItem>
                {classes.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)} sx={{ fontFamily: '"Outfit", sans-serif' }}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ fontFamily: '"Outfit", sans-serif' }}>{t('food.month_label')}</InputLabel>
                <Select value={srcMonth} label={t('food.month_label')} onChange={(e) => setSrcMonth(e.target.value)}
                  sx={{ fontFamily: '"Outfit", sans-serif', borderRadius: '8px', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: AMBER } }}>
                  {MONTH_NAMES.map((name, i) => (
                    <MenuItem key={i} value={i} disabled={i === month && srcYear === year} sx={{ fontFamily: '"Outfit", sans-serif' }}>{name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField label={t('food.year_label')} size="small" type="number" value={srcYear}
                onChange={(e) => setSrcYear(Number(e.target.value))}
                sx={{ width: 100, '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: AMBER }, '& .MuiInputLabel-root.Mui-focused': { color: AMBER } }}
                inputProps={{ min: 2020, max: 2040 }} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setCopyOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b', borderRadius: '8px' }}>{t('food.cancel')}</Button>
            <Button variant="contained" onClick={handleCopy} disabled={copying}
              startIcon={copying ? <CircularProgress size={14} color="inherit" /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
              sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: `linear-gradient(135deg, ${NAV_BG} 0%, #163571 100%)`, '&:hover': { background: '#1e3a7a' } }}>
              {copying ? t('food.copying') : t('food.copy')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Clear month dialog ── */}
        <Dialog open={clearOpen} onClose={() => setClearOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
          <DialogTitle sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: '#dc2626', pb: 1 }}>
            {t('food.clear_dialog_title', { month: MONTH_NAMES[month], year })}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.875rem', color: '#475569' }}>
              {t('food.clear_dialog_body', { month: MONTH_NAMES[month], year })}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setClearOpen(false)} sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#64748b', borderRadius: '8px' }}>{t('food.cancel')}</Button>
            <Button variant="contained" onClick={handleClear} disabled={clearing}
              startIcon={clearing ? <CircularProgress size={14} color="inherit" /> : <DeleteSweepIcon sx={{ fontSize: 16 }} />}
              sx={{ fontFamily: '"Outfit", sans-serif', textTransform: 'none', fontWeight: 600, borderRadius: '8px', background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>
              {clearing ? t('food.clearing') : t('food.clear_confirm')}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={!!toast.msg} autoHideDuration={20000} onClose={() => setToast(t => ({ ...t, msg: '' }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={toast.severity} onClose={() => setToast(t => ({ ...t, msg: '' }))} sx={{ fontFamily: '"Outfit", sans-serif' }}>{toast.msg}</Alert>
        </Snackbar>

      </Container>
    </Box>
  );
};

export default FoodPage;
